import React from "react";
import { RotateCw } from "lucide-react";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { useParams } from "react-router-dom";
import { useGetMeterSummaryDashboard } from "../../hooks";

export const ReadCycleProfileCard: React.FC<any> = ({ data }) => {
  const { cycle_id } = useParams();
  const { data: meterSummaryDashboardData } =
    useGetMeterSummaryDashboard(cycle_id);

  const getStatusClass = (status: string) => {
    const s = status ? String(status).toUpperCase() : "";
    switch (s) {
      case "COMPLETED":
      case "COMPLETE":
        return (
          <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 border border-blue-200">
            Completed
          </span>
        );
      case "SCHEDULED":
        return (
          <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-600 border border-emerald-200">
            Scheduled
          </span>
        );
      case "UNSCHEDULED":
        return (
          <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-600 border border-amber-200">
            Unscheduled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 border border-blue-200">
            {data?.statusDisplay || status || "Completed"}
          </span>
        );
    }
  };

  const routeCount = meterSummaryDashboardData?.totalRoute;

  const resourceCount = meterSummaryDashboardData?.cycleWiseTotal ?? 0;

  const cycleId = data?.meterCycleCode || "N/A";

  const cycleOverviewItems = [
    {
      label: "Cycle Type",
      value: data?.frequencyDisplay || "N/A",
    },
    {
      label: "Start Date",
      value: data?.startDate || "N/A",
    },
    {
      label: "End Date",
      value: data?.endDate || "N/A",
    },
    {
      label: "Routes",
      value: routeCount,
    },
  ];

  const auditItems = [
    {
      label: "Created By",
      value: data?.createdUserRemoteName || "N/A",
    },
    {
      label: "Created On",
      value: data?.createdDate || "N/A",
    },
    {
      label: "Last Updated By",
      value: data?.lastModifiedUserRemoteName || "N/A",
    },
    {
      label: "Last Updated On",
      value: data?.lastModifiedDate || "N/A",
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-2xs space-y-8">
      {/* ---- Header Section ---- */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Left Info */}
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-blue-50/80 flex items-center justify-center text-blue-600 shrink-0 mt-0.5">
            <RotateCw className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mt-0.5 mb-2">
              {data?.meterCycleName || "N/A"}
            </h2>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-600">
                {data?.frequencyDisplay || "N/A"}
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                {routeCount} {routeCount === 1 ? "Route" : "Routes"}
              </span>
            </div>
          </div>
        </div>

        {/* Right Stat Box */}
        <div className="border border-gray-200/90 rounded-xl flex items-center divide-x divide-gray-200/90 bg-white overflow-hidden self-start sm:self-auto shrink-0 shadow-2xs">
          <div className="px-5 py-3 text-left flex flex-col justify-center min-w-[100px]">
            <span className="text-xs text-gray-400 font-normal mb-1.5">
              Status
            </span>
            <div>{getStatusClass(data?.status)}</div>
          </div>
          <div className="px-6 py-3 text-left flex flex-col justify-center min-w-[80px]">
            <span className="text-xs text-gray-400 font-normal mb-1.5">
              Routes
            </span>
            <span className="text-xl font-bold text-gray-900">
              {routeCount}
            </span>
          </div>
          <div className="px-6 py-3 text-left flex flex-col justify-center min-w-[90px]">
            <span className="text-xs text-gray-400 font-normal mb-1.5">
              Resources
            </span>
            <span className="text-xl font-bold text-gray-900">
              {resourceCount}
            </span>
          </div>
        </div>
      </div>

      {/* ---- Cycle Overview Section ---- */}
      <div className="pt-2">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">
          Cycle Overview
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
          {cycleOverviewItems.map((item) => (
            <ValueLabel
              key={item.label}
              label={item.label}
              value={item.value}
            />
          ))}
        </div>
      </div>

      {/* ---- Audit Section ---- */}
      <div className="pt-2">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Audit</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
          {auditItems.map((item) => (
            <ValueLabel
              key={item.label}
              label={item.label}
              value={item.value}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
